import React, { useState, useEffect } from 'react';
import { Game, User } from '../types';
import { getUserGames, updateGameLevel, updateGameDescription, updateGameNotes, initializeUserGames, resetUserGames } from '../services/gameService';
import GameRow from './GameRow';
import './GameTable.css';

interface GameTableProps {
  selectedUser: User;
}

const GameTable: React.FC<GameTableProps> = ({ selectedUser }) => {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGames();
  }, [selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGames = async () => {
    try {
      setLoading(true);
      let gameList = await getUserGames(selectedUser.id);
      
      // Eğer kullanıcının hiç oyunu yoksa varsayılan oyunları ekle
      if (gameList.length === 0) {
        await initializeUserGames(selectedUser.id);
        gameList = await getUserGames(selectedUser.id);
      }
      
      setGames(gameList);
      setError(null);
    } catch (err) {
      setError('Oyunlar yüklenirken bir hata oluştu');
      console.error('Error loading games:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLevelChange = async (gameId: string, newLevel: number) => {
    try {
      const success = await updateGameLevel(gameId, newLevel);
      if (success) {
        setGames(games.map(game => 
          game.id === gameId ? { ...game, currentLevel: newLevel } : game
        ));
      } else {
        setError('Level güncellenirken hata oluştu');
      }
    } catch (err) {
      setError('Level güncellenirken hata oluştu');
    }
  };

  const handleDescriptionChange = async (gameId: string, description: string) => {
    try {
      const success = await updateGameDescription(gameId, description);
      if (success) {
        setGames(games.map(game => 
          game.id === gameId ? { ...game, description } : game
        ));
      } else {
        setError('Açıklama güncellenirken hata oluştu');
      }
    } catch (err) {
      setError('Açıklama güncellenirken hata oluştu');
    }
  };

  const handleNotesChange = async (gameId: string, notes: string) => {
    try {
      const success = await updateGameNotes(gameId, notes);
      if (success) {
        setGames(games.map(game => 
          game.id === gameId ? { ...game, notes } : game
        ));
      } else {
        setError('Notlar güncellenirken hata oluştu');
      }
    } catch (err) {
      setError('Notlar güncellenirken hata oluştu');
    }
  };

  const handleResetGames = async () => {
    if (window.confirm(`${selectedUser.displayName} için tüm oyunları sıfırlamak istediğinizden emin misiniz? Bu işlem geri alınamaz!`)) {
      try {
        setLoading(true);
        const success = await resetUserGames(selectedUser.id);
        if (success) {
          await loadGames();
        } else {
          setError('Oyunlar sıfırlanırken hata oluştu');
        }
      } catch (err) {
        setError('Oyunlar sıfırlanırken hata oluştu');
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Oyunlar yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={loadGames} className="retry-button">
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className="game-table-container">
      <div className="table-header">
        <h1>🎮 {selectedUser.displayName} - Oyun Takip Tablosu</h1>
        <p>Toplam {games.length} oyun</p>
        {games.length > 16 && (
          <div className="reset-warning">
            ⚠️ Bu kullanıcıda {games.length} oyun var (normal: 16). Eğer eski ve yeni oyunlar karışmışsa:
            <button onClick={handleResetGames} className="reset-button">
              🔄 Oyunları Yeni Liste ile Sıfırla
            </button>
          </div>
        )}
      </div>
      
      <div className="table-wrapper">
        <table className="game-table">
          <thead>
            <tr>
              <th className="col-index">#</th>
              <th className="col-name">Oyun Adı</th>
              <th className="col-level">Mevcut Level</th>
              <th className="col-max-level">Max Level</th>
              <th className="col-progress">İlerleme</th>
              <th className="col-description">Açıklama</th>
              <th className="col-notes">Notlar</th>
              <th className="col-last-played">Son Oynanma</th>
            </tr>
          </thead>
          <tbody>
            {games.map((game, index) => (
              <GameRow
                key={game.id}
                game={game}
                index={index + 1}
                onLevelChange={handleLevelChange}
                onDescriptionChange={handleDescriptionChange}
                onNotesChange={handleNotesChange}
              />
            ))}
          </tbody>
        </table>
      </div>
      
      {games.length === 0 && !loading && (
        <div className="empty-state">
          <p>{selectedUser.displayName} için henüz hiç oyun eklenmemiş</p>
          <button onClick={loadGames} className="load-default-button">
            Varsayılan Oyunları Yükle
          </button>
        </div>
      )}
    </div>
  );
};

export default GameTable;
